"use client"

import { useState, useEffect } from "react"
import { Modal, Form, Button, Alert, Spinner } from "react-bootstrap"
import { apiRequest, endpoints } from "../utils/api"
import { toast } from "react-hot-toast"

const CropEditModal = ({ show, onHide, crop, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    min_temp: "",
    max_temp: "",
    growth_period_days: "",
    soil_type: "",
    photo: "",
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [photoFile, setPhotoFile] = useState(null)
  const [photoPreview, setPhotoPreview] = useState("")
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  useEffect(() => {
    if (crop) {
      setFormData({
        name: crop.name || "",
        min_temp: crop.min_temp || "",
        max_temp: crop.max_temp || "",
        growth_period_days: crop.growth_period_days || "",
        soil_type: crop.soil_type || "",
        photo: crop.photo || "",
      })
      setPhotoPreview(crop.photo || "")
    } else {
      setFormData({
        name: "",
        min_temp: "",
        max_temp: "",
        growth_period_days: "",
        soil_type: "",
        photo: "",
      })
      setPhotoPreview("")
    }
    setError("")
    setPhotoFile(null)
  }, [crop, show])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file")
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB")
      return
    }

    setPhotoFile(file)

    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setPhotoPreview(e.target.result)
    }
    reader.readAsDataURL(file)
  }

  const uploadPhoto = async () => {
    if (!photoFile) return null

    setUploadingPhoto(true)
    try {
      const formData = new FormData()
      formData.append("photo", photoFile)

      const token = localStorage.getItem("adminToken")
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8787"}${endpoints.upload}/crop-photo`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      )

      if (!response.ok) {
        throw new Error("Upload failed")
      }

      const result = await response.json()
      return result.photoUrl
    } catch (error) {
      toast.error("Failed to upload photo")
      throw error
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      // Validate form data
      if (!formData.name || !formData.min_temp || !formData.max_temp || !formData.growth_period_days) {
        throw new Error("Please fill in all required fields")
      }

      if (Number.parseFloat(formData.min_temp) >= Number.parseFloat(formData.max_temp)) {
        throw new Error("Maximum temperature must be greater than minimum temperature")
      }

      let photoUrl = formData.photo
      if (photoFile) {
        photoUrl = await uploadPhoto()
      }

      const payload = {
        ...formData,
        min_temp: Number.parseFloat(formData.min_temp),
        max_temp: Number.parseFloat(formData.max_temp),
        growth_period_days: Number.parseInt(formData.growth_period_days),
        photo: photoUrl,
      }

      const token = localStorage.getItem("adminToken")

      if (crop) {
        await apiRequest(`${endpoints.crops}/${crop.id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        })
        toast.success("Crop updated successfully")
      } else {
        await apiRequest(endpoints.crops, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        })
        toast.success("Crop created successfully")
      }

      onSave()
      onHide()
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{crop ? "Edit Crop" : "Add New Crop"}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Crop Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="e.g., Tomato, Wheat, Corn"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Soil Type</Form.Label>
                <Form.Control
                  type="text"
                  name="soil_type"
                  value={formData.soil_type}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="e.g., Loamy, Sandy, Clay"
                />
              </Form.Group>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <Form.Group>
                <Form.Label>Crop Photo</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  disabled={loading || uploadingPhoto}
                />
                <Form.Text className="text-muted">Upload a photo of the crop (max 5MB, JPG/PNG/GIF)</Form.Text>
              </Form.Group>

              {photoPreview && (
                <div className="mt-2">
                  <img
                    src={photoPreview || "/placeholder.svg"}
                    alt="Crop preview"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "150px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid #ddd",
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Minimum Temperature (°C) *</Form.Label>
                <Form.Control
                  type="number"
                  name="min_temp"
                  value={formData.min_temp}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  step="0.1"
                  placeholder="e.g., 10"
                />
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Maximum Temperature (°C) *</Form.Label>
                <Form.Control
                  type="number"
                  name="max_temp"
                  value={formData.max_temp}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  step="0.1"
                  placeholder="e.g., 35"
                />
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group className="mb-3">
                <Form.Label>Growth Period (days) *</Form.Label>
                <Form.Control
                  type="number"
                  name="growth_period_days"
                  value={formData.growth_period_days}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  min="1"
                  placeholder="e.g., 110"
                />
              </Form.Group>
            </div>
          </div>

          <div className="bg-light p-3 rounded">
            <h6 className="mb-2">Temperature Range Guidelines:</h6>
            <small className="text-muted">
              • Minimum temperature: Lowest temperature the crop can tolerate
              <br />• Maximum temperature: Highest temperature the crop can tolerate
              <br />• Growth period: Total days from planting to harvest
            </small>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="success" disabled={loading || uploadingPhoto}>
            {loading || uploadingPhoto ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {uploadingPhoto ? "Uploading..." : crop ? "Updating..." : "Creating..."}
              </>
            ) : crop ? (
              "Update Crop"
            ) : (
              "Create Crop"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default CropEditModal
